import { useState, useEffect } from 'react';
import axios from 'axios';
import '../../styles/internal/App.css';
import { SlArrowDown, SlArrowUp } from 'react-icons/sl';
import Sidebar from '../../components/internal/Sidebar';
import { useNavigate } from 'react-router-dom';
import { useEnvValue } from '../../config/env';

const meses = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

export default function Dashboard() {
  const navigate = useNavigate();
  const API_BASE_URL = useEnvValue('REACT_APP_URL_API');
  const X_API_KEY = useEnvValue('REACT_APP_X_API_KEY');

  const [anoSelecionado, setAnoSelecionado] = useState(null);
  const [mesExpandido, setMesExpandido] = useState(null);
  const [mensagemCopiada, setMensagemCopiada] = useState(false);
  const [dadosNotas, setDadosNotas] = useState({});
  const [anosDisponiveis, setAnosDisponiveis] = useState([]);
  const [dadosSelecionados, setDadosSelecionados] = useState([]);
  const [erroToken, setErroToken] = useState(false);
  const [cpf, setCpf] = useState('');
  const [mensagemSemNotas, setMensagemSemNotas] = useState('');

  const toggleMes = (mes) => {
    setMesExpandido((prev) => (prev === mes ? null : mes));
  };

  const copiarChave = () => {
    const chave = dadosSelecionados[0]?.F2_CHVNFE || '';
    if (!chave) return;

    navigator.clipboard.writeText(chave).then(() => {
      setMensagemCopiada(true);
      setTimeout(() => setMensagemCopiada(false), 3000);
    });
  };

  const getMesNumero = (mesNome) => {
    const index = meses.indexOf(mesNome);
    return (index + 1).toString().padStart(2, '0');
  };

  useEffect(() => {
    async function fetchNotas() {
      const token = localStorage.getItem('access_token');
      const storedCpf = localStorage.getItem('cpf');
      const dataNascimento = localStorage.getItem('dataNascimento');
      setCpf(storedCpf);

      try {
        const csrfResponse = await axios.get(`${API_BASE_URL}/csrf/token`, {
          withCredentials: true
        });

        const csrfToken = csrfResponse.data?.csrfToken;

        const response = await axios.post(
          `${API_BASE_URL}/notas`,
          {
            document: storedCpf,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'x-api-key': X_API_KEY,
              'X-CSRF-Token': csrfToken
            },
            withCredentials: true
          }
        );

        const data = response.data;
        setDadosNotas(data);

        const anosDisponiveisNoJson = Object.keys(data).map((a) => parseInt(a)).sort((a, b) => b - a);
        const anoAtual = new Date().getFullYear();
        const ultimos5Anos = Array.from({ length: 5 }, (_, i) => anoAtual - i);

        const anosComNota = ultimos5Anos.filter(ano => anosDisponiveisNoJson.includes(ano));
        const anosFinais = anosComNota.length > 0
          ? anosComNota.map(String)
          : anosDisponiveisNoJson.map(String);

        setAnosDisponiveis(anosFinais);
        setAnoSelecionado(anosFinais[0]);
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate('/');
        } else if (error.response?.status === 404) {
          setErroToken(false);
          setMensagemSemNotas('O cliente não possui notas fiscais válidas.');
          setDadosNotas({});
          setAnosDisponiveis([]);
          setAnoSelecionado(null);
        } else {
          setErroToken(true);
        }
      }
    }

    fetchNotas();
  }, [navigate]);

  useEffect(() => {
    if (anoSelecionado && mesExpandido) {
      const mesNumero = getMesNumero(mesExpandido);
      const dadosMes = dadosNotas[anoSelecionado]?.[mesNumero] || [];
      setDadosSelecionados(dadosMes);
    } else {
      setDadosSelecionados([]);
    }
  }, [anoSelecionado, mesExpandido, dadosNotas]);

  return (
    <div className="dashboard-container">
      <Sidebar />

      <div className="dashboard-shell">
        <div className="dashboard-hero">
          <div>
            <p className="eyebrow">Portal Guardian</p>
            <h1>Notas fiscais em um só lugar</h1>
            <p>Confira os seus registros, selecione o ano e acesse a chave de acesso em poucos cliques.</p>
          </div>

          <div className="hero-badge">
            <span className="hero-badge-label">Cliente</span>
            <strong>{cpf || '---'}</strong>
          </div>
        </div>

        {mensagemSemNotas && (
          <div className="dashboard-alert alert-warning">
            {mensagemSemNotas}
          </div>
        )}

        {erroToken ? (
          <div className="dashboard-alert alert-error">
            Ocorreu um erro ao carregar os dados. Tente novamente mais tarde.
          </div>
        ) : (
          <>
            <div className="dashboard-toolbar">
              <div className="ano-selector">
                {anosDisponiveis.map((ano) => (
                  <button
                    key={ano}
                    className={`ano-button ${anoSelecionado === ano ? 'ativo' : ''}`}
                    onClick={() => setAnoSelecionado(ano)}
                  >
                    {ano}
                  </button>
                ))}
                <button
                  className={`ano-button ${anoSelecionado === 'Skeelo' ? 'skeelo-ativo' : ''}`}
                  onClick={() => setAnoSelecionado('Skeelo')}
                >
                  Skeelo
                </button>
              </div>
              <span className="toolbar-hint">Selecione um período para abrir os detalhes</span>
            </div>

            {anoSelecionado === 'Skeelo' ? (
              <div className="skeelo-info">
                <p>
                  O Skeelo é o maior aplicativo de leitura do Brasil com os livros mais vendidos do mercado.
                  No aplicativo você encontra os títulos em formatos de e-book, audiobook e minibook.
                  Clientes Guardian têm diferentes modalidades de acesso ao Skeelo, de acordo com o seu plano.
                  Acesse o site da <a href='https://www.skeelo.com.br'>Skeelo</a>, se cadastre e aproveite o benefício.
                </p>
              </div>
            ) : (
              <div className="accordion">
                {meses
                  .filter((_, index) => {
                    const mesNumero = (index + 1).toString().padStart(2, '0');
                    return dadosNotas[anoSelecionado]?.[mesNumero]?.length > 0;
                  })
                  .map((mes) => (
                    <div key={mes} className="mes-item">
                      <button
                        className={`mes-button ${mesExpandido === mes ? 'ativo' : ''}`}
                        onClick={() => toggleMes(mes)}
                      >
                        <span>{mes}</span>
                        <span className="icone">
                          {mesExpandido === mes ? <SlArrowUp /> : <SlArrowDown />}
                        </span>
                      </button>

                      {mesExpandido === mes && dadosSelecionados.length > 0 && (
                        <div className="conteudo">
                          <div className="linha">
                            <div className="field-group">
                              <label>Razão Social</label>
                              <input value={dadosSelecionados[0]?.RAZAO_SOCIAL || '---'} disabled />
                            </div>
                            <div className="field-group">
                              <label>CPF</label>
                              <input value={cpf || '---'} disabled />
                            </div>
                          </div>

                          <div className="field-group full-width">
                            <label>Chave de Acesso</label>
                            <input value={dadosSelecionados[0]?.F2_CHVNFE || '---'} disabled />
                          </div>

                          <p className="aviso">
                            Copie a Chave de Acesso e cole no site da SEFAZ para verificar a autorização da Nota Fiscal.
                          </p>

                          <div className="botoes">
                            <button className="button" onClick={copiarChave}>
                              Copiar chave de acesso
                            </button>

                            <a
                              className="button button-secondary"
                              href="https://www.nfe.fazenda.gov.br/portal/consulta.aspx"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Site SEFAZ
                            </a>
                          </div>

                          {mensagemCopiada && (
                            <div className="alerta-sucesso">
                              <span className="barra-lateral" />
                              <span>Chave copiada com sucesso</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
